import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import DOMPurify from 'dompurify';
import {
  ArrowLeft, RefreshCw, Sparkles, FileText, Check, X, Clock, Eye,
  Search, Zap, Calendar, Settings, ChevronDown, ChevronUp, BookOpen, Globe, Loader2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-blue-100 text-blue-700', icon: FileText },
  under_review: { label: 'Under Review', color: 'bg-yellow-100 text-yellow-700', icon: Eye },
  approved: { label: 'Approved', color: 'bg-green-100 text-green-700', icon: Check },
  published: { label: 'Published', color: 'bg-emerald-100 text-emerald-700', icon: BookOpen },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700', icon: X }
};

const AIBlogDashboard = () => {
  const navigate = useNavigate();
  const [drafts, setDrafts] = useState([]);
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [researching, setResearching] = useState(false);
  const [topics, setTopics] = useState([]);
  const [selectedDraft, setSelectedDraft] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [showSchedule, setShowSchedule] = useState(false);
  const [topicCategory, setTopicCategory] = useState('all');
  const [customTopic, setCustomTopic] = useState('');
  const [customCategory, setCustomCategory] = useState('finance');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [draftsRes, scheduleRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/admin/ai-blog/drafts${statusFilter ? `?status=${statusFilter}` : ''}`),
        fetch(`${BACKEND_URL}/api/admin/ai-blog/schedule`)
      ]);
      if (draftsRes.ok) setDrafts((await draftsRes.json()).drafts || []);
      if (scheduleRes.ok) setSchedule(await scheduleRes.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const researchTopics = async () => {
    setResearching(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/ai-blog/research-topics`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ category: topicCategory })
      });
      if (res.ok) {
        const data = await res.json();
        setTopics(data.topics || []);
      } else {
        const err = await res.json();
        alert(`Research failed: ${err.detail || 'Unknown error'}`);
      }
    } catch (e) { alert(`Error: ${e.message}`); }
    finally { setResearching(false); }
  };

  const generateFromTopic = async (topic) => {
    setGenerating(true);
    try {
      const keywords = (topic.keywords || []).map(k => typeof k === 'string' ? k : k.word);
      const res = await fetch(`${BACKEND_URL}/api/admin/ai-blog/generate`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ topic: topic.title, keywords, category: topic.category || 'finance' })
      });
      if (res.ok) {
        const draft = await res.json();
        setSelectedDraft(draft);
        fetchData();
        alert('Blog draft generated!');
      } else {
        const err = await res.json();
        alert(`Generation failed: ${err.detail || 'Unknown error'}`);
      }
    } catch (e) { alert(`Error: ${e.message}`); }
    finally { setGenerating(false); }
  };

  const generateCustom = async () => {
    if (!customTopic.trim()) return;
    setGenerating(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/ai-blog/generate`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ topic: customTopic, category: customCategory })
      });
      if (res.ok) {
        const draft = await res.json();
        setSelectedDraft(draft);
        setCustomTopic('');
        fetchData();
        alert('Blog draft generated!');
      } else {
        const err = await res.json();
        alert(`Generation failed: ${err.detail || 'Unknown error'}`);
      }
    } catch (e) { alert(`Error: ${e.message}`); }
    finally { setGenerating(false); }
  };

  const autoGenerate = async () => {
    setGenerating(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/ai-blog/auto-generate`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        setSelectedDraft(data.draft);
        setTopics(data.researched_topics || []);
        fetchData();
        alert(`Blog generated on: "${data.draft?.title}"`);
      } else {
        const err = await res.json();
        alert(`Auto-generate failed: ${err.detail || 'Unknown error'}`);
      }
    } catch (e) { alert(`Error: ${e.message}`); }
    finally { setGenerating(false); }
  };

  const updateDraftStatus = async (draftId, newStatus, reason = '') => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/ai-blog/drafts/${draftId}/status`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus, rejection_reason: reason })
      });
      if (res.ok) fetchData();
    } catch (e) { console.error(e); }
  };

  const publishDraft = async (draftId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/ai-blog/publish/${draftId}`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        alert(`Published! View at /blog/${data.slug}`);
        fetchData();
      } else {
        const err = await res.json();
        alert(err.detail || 'Publish failed');
      }
    } catch (e) { alert(`Error: ${e.message}`); }
  };

  const updateSchedule = async (updates) => {
    try {
      const newSchedule = { ...schedule, ...updates };
      await fetch(`${BACKEND_URL}/api/admin/ai-blog/schedule`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newSchedule)
      });
      setSchedule(newSchedule);
    } catch (e) { console.error(e); }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={() => navigate('/admin/dashboard')} size="sm">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900" data-testid="ai-blog-title">AI Blog Engine</h1>
              <p className="text-sm text-gray-600">Auto-generate SEO-optimized blogs</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button onClick={autoGenerate} disabled={generating} size="sm" className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white" data-testid="auto-generate-btn">
              <Zap className={`w-4 h-4 mr-1 ${generating ? 'animate-pulse' : ''}`} />
              {generating ? 'Generating...' : 'Auto Generate'}
            </Button>
            <Button onClick={() => setShowSchedule(!showSchedule)} variant="outline" size="sm">
              <Settings className="w-4 h-4 mr-1" /> Schedule
            </Button>
            <Button onClick={fetchData} variant="outline" size="sm" disabled={loading}>
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        {/* Schedule Settings */}
        {showSchedule && schedule && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2"><Calendar className="w-4 h-4" /> Blog Schedule</CardTitle>
              <p className="text-xs text-gray-500">Automatically generates country-targeted blogs daily</p>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="grid sm:grid-cols-4 gap-4 items-end">
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Blogs per week</label>
                  <select value={schedule.frequency} onChange={e => updateSchedule({ frequency: Number(e.target.value) })} className="w-full border rounded-lg px-3 py-2 text-sm">
                    {[1,2,3,4,5,7].map(n => <option key={n} value={n}>{n}/week</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Schedule Time (UTC)</label>
                  <select value={schedule.time || '06:00'} onChange={e => updateSchedule({ time: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm">
                    {['04:00','05:00','06:00','07:00','08:00','09:00','10:00'].map(t => <option key={t} value={t}>{t} UTC</option>)}
                  </select>
                </div>
                <div className="flex flex-col gap-2">
                  <Button onClick={() => updateSchedule({ enabled: !schedule.enabled })} size="sm"
                    data-testid="schedule-toggle-btn"
                    className={schedule.enabled ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-600'}>
                    Scheduler: {schedule.enabled ? 'ON' : 'OFF'}
                  </Button>
                  <Button onClick={() => updateSchedule({ auto_publish: !schedule.auto_publish })} size="sm"
                    data-testid="auto-publish-toggle-btn"
                    className={schedule.auto_publish ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'}>
                    Auto-Publish: {schedule.auto_publish ? 'ON' : 'OFF'}
                  </Button>
                </div>
                <div className="text-sm text-gray-500">
                  {schedule.last_generated && <p>Last: {new Date(schedule.last_generated).toLocaleDateString('en-IN')}</p>}
                  {schedule.last_country && <p>Country: {schedule.last_country}</p>}
                  <p>Generated: {schedule.total_generated || 0} | Published: {schedule.total_published || 0}</p>
                </div>
              </div>

              {/* Country Schedule */}
              <div>
                <label className="text-xs font-medium text-gray-600 mb-2 block">Country Targeting Schedule</label>
                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
                  {Object.entries(schedule.country_schedule || {
                    Monday: 'UK', Tuesday: 'US', Wednesday: 'UAE', 
                    Thursday: 'Singapore', Friday: 'Japan', Saturday: 'India', Sunday: 'General'
                  }).map(([day, country]) => (
                    <div key={day} className="text-center bg-gray-50 rounded-xl p-3 border">
                      <div className="text-xs font-semibold text-gray-800">{day.slice(0,3)}</div>
                      <div className="flex items-center justify-center gap-1 mt-1">
                        <Globe className="w-3 h-3 text-emerald-600" />
                        <span className="text-xs text-emerald-700 font-medium">{country}</span>
                      </div>
                      <Button size="sm" variant="ghost" className="h-6 text-xs mt-1 text-purple-600"
                        data-testid={`gen-country-${country.toLowerCase()}`}
                        onClick={async () => {
                          setGenerating(true);
                          try {
                            const res = await fetch(`${BACKEND_URL}/api/admin/ai-blog/generate-for-country`, {
                              method: 'POST', headers: {'Content-Type': 'application/json'},
                              body: JSON.stringify({ country })
                            });
                            if (res.ok) {
                              const data = await res.json();
                              fetchData();
                              alert(`Generated: "${data.draft?.title}" for ${country}`);
                            }
                          } catch(e) { alert(e.message); }
                          finally { setGenerating(false); }
                        }}>
                        {generating ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Generate'}
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left: Topic Research + Generation */}
          <div className="space-y-6">
            {/* Research Trending Topics */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2"><Sparkles className="w-4 h-4 text-purple-500" /> Research Topics</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex gap-2">
                  <select value={topicCategory} onChange={e => setTopicCategory(e.target.value)} className="border rounded-lg px-3 py-2 text-sm flex-1">
                    <option value="all">All Categories</option>
                    <option value="fdi">FDI & India Entry</option>
                    <option value="finance">Finance</option>
                    <option value="tax">Tax & GST</option>
                    <option value="compliance">Compliance</option>
                    <option value="quick_read">Quick Reads</option>
                    <option value="ai">AI in Finance</option>
                    <option value="budgets">Budgets</option>
                  </select>
                  <Button onClick={researchTopics} disabled={researching} size="sm" className="bg-purple-600 text-white" data-testid="research-topics-btn">
                    <Search className={`w-4 h-4 mr-1 ${researching ? 'animate-spin' : ''}`} />
                    {researching ? '...' : 'Find'}
                  </Button>
                </div>

                {topics.length > 0 && (
                  <div className="space-y-2 max-h-80 overflow-y-auto">
                    {topics.map((t, i) => (
                      <div key={t.title?.slice(0,30) || `topic-${i}`} className="border rounded-lg p-3 hover:border-purple-300 transition-colors" data-testid={`topic-${i}`}>
                        <p className="font-medium text-sm text-gray-900 mb-1">{t.title}</p>
                        <p className="text-xs text-gray-500 mb-2">{t.brief}</p>
                        <div className="flex items-center justify-between">
                          <div className="flex gap-1">
                            <span className="text-xs bg-gray-100 px-2 py-0.5 rounded">{t.category}</span>
                            <span className="text-xs bg-gray-100 px-2 py-0.5 rounded">{t.difficulty}</span>
                          </div>
                          <Button size="sm" onClick={() => generateFromTopic(t)} disabled={generating} className="h-7 text-xs bg-purple-600 text-white" data-testid={`generate-topic-${i}`}>
                            <Sparkles className="w-3 h-3 mr-1" /> Generate
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Custom Topic */}
            <Card>
              <CardHeader><CardTitle className="text-base">Custom Topic</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <Input value={customTopic} onChange={e => setCustomTopic(e.target.value)} placeholder="Enter a blog topic..." data-testid="custom-topic-input" />
                <div className="flex gap-2">
                  <select value={customCategory} onChange={e => setCustomCategory(e.target.value)} className="border rounded-lg px-3 py-2 text-sm">
                    <option value="fdi">FDI & India Entry</option>
                    <option value="finance">Finance</option>
                    <option value="tax">Tax</option>
                    <option value="compliance">Compliance</option>
                    <option value="quick_read">Quick Read</option>
                    <option value="ai">AI</option>
                    <option value="budgets">Budgets</option>
                  </select>
                  <Button onClick={generateCustom} disabled={generating || !customTopic.trim()} className="flex-1 bg-emerald-600 text-white" data-testid="generate-custom-btn">
                    <Sparkles className="w-4 h-4 mr-1" /> Generate Blog
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Middle + Right: Drafts List + Preview */}
          <div className="lg:col-span-2 space-y-4">
            {/* Status Filter */}
            <div className="flex gap-2 overflow-x-auto pb-1">
              {['', 'draft', 'under_review', 'approved', 'published', 'rejected'].map(s => (
                <button key={s} onClick={() => setStatusFilter(s)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap ${statusFilter === s ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                  {s ? STATUS_CONFIG[s]?.label : 'All'}
                </button>
              ))}
            </div>

            {/* Drafts */}
            <Card>
              <CardContent className="p-0">
                {loading ? (
                  <div className="flex items-center justify-center py-12"><RefreshCw className="w-6 h-6 animate-spin text-gray-400" /></div>
                ) : drafts.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <FileText className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                    <p>No drafts yet</p>
                    <p className="text-sm mt-1">Research topics or auto-generate to get started</p>
                  </div>
                ) : (
                  <div className="divide-y">
                    {drafts.map(d => {
                      const cfg = STATUS_CONFIG[d.status] || STATUS_CONFIG.draft;
                      return (
                        <div key={d.draft_id} onClick={() => setSelectedDraft(d)}
                          data-testid={`draft-${d.draft_id}`}
                          className={`p-4 hover:bg-gray-50 cursor-pointer ${selectedDraft?.draft_id === d.draft_id ? 'bg-purple-50 border-l-4 border-purple-500' : ''}`}>
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-gray-900 truncate">{d.title}</p>
                              <p className="text-xs text-gray-500 mt-1 line-clamp-1">{d.excerpt}</p>
                              <div className="flex items-center gap-2 mt-2">
                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cfg.color}`}>{cfg.label}</span>
                                <span className="text-xs text-gray-400">{d.category}</span>
                                <span className="text-xs text-gray-400">{d.generated_at ? new Date(d.generated_at).toLocaleDateString('en-IN') : ''}</span>
                              </div>
                            </div>
                            {/* Action buttons based on status */}
                            <div className="flex gap-1 flex-shrink-0">
                              {d.status === 'draft' && (
                                <Button size="sm" onClick={(e) => { e.stopPropagation(); updateDraftStatus(d.draft_id, 'under_review'); }}
                                  className="h-7 text-xs bg-yellow-500 text-white" data-testid={`review-btn-${d.draft_id}`}>
                                  <Eye className="w-3 h-3 mr-1" /> Review
                                </Button>
                              )}
                              {d.status === 'under_review' && (
                                <>
                                  <Button size="sm" onClick={(e) => { e.stopPropagation(); updateDraftStatus(d.draft_id, 'approved'); }}
                                    className="h-7 text-xs bg-green-600 text-white" data-testid={`approve-btn-${d.draft_id}`}>
                                    <Check className="w-3 h-3" />
                                  </Button>
                                  <Button size="sm" onClick={(e) => { e.stopPropagation(); updateDraftStatus(d.draft_id, 'rejected', 'Needs revision'); }}
                                    className="h-7 text-xs bg-red-500 text-white">
                                    <X className="w-3 h-3" />
                                  </Button>
                                </>
                              )}
                              {d.status === 'approved' && (
                                <Button size="sm" onClick={(e) => { e.stopPropagation(); publishDraft(d.draft_id); }}
                                  className="h-7 text-xs bg-emerald-600 text-white" data-testid={`publish-btn-${d.draft_id}`}>
                                  <BookOpen className="w-3 h-3 mr-1" /> Publish
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Draft Preview */}
            {selectedDraft && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Preview: {selectedDraft.title}</CardTitle>
                  <CardDescription>
                    By {selectedDraft.author} | {selectedDraft.category} | SEO: {selectedDraft.seo_title}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* SEO Info */}
                  <div className="bg-gray-50 rounded-lg p-3 space-y-1">
                    <p className="text-xs font-medium text-gray-600">SEO Title: <span className="text-gray-800">{selectedDraft.seo_title}</span></p>
                    <p className="text-xs font-medium text-gray-600">Meta: <span className="text-gray-800">{selectedDraft.seo_description}</span></p>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {(selectedDraft.keywords || []).map((kw, i) => (
                        <span key={kw || `kw-${i}`} className="bg-purple-100 text-purple-700 text-xs px-2 py-0.5 rounded">{kw}</span>
                      ))}
                    </div>
                  </div>

                  {/* Content Preview */}
                  <div className="prose prose-sm max-w-none max-h-96 overflow-y-auto border rounded-lg p-4"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selectedDraft.content) }} data-testid="draft-content-preview" />

                  {/* FAQ */}
                  {selectedDraft.faq && selectedDraft.faq.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-sm mb-2">FAQ Section</h4>
                      {selectedDraft.faq.map((f, i) => (
                        <div key={h || `heading-${i}`} className="mb-2">
                          <p className="text-sm font-medium text-gray-900">Q: {f.q}</p>
                          <p className="text-sm text-gray-600">A: {f.a}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIBlogDashboard;
